import { createAction, handleActions, Reducer } from 'redux-actions';

import { TwoFactorConfig } from '@api/types';
import { SelectorState, Trace, TraceStatus } from '@redux/types';

type TwoFactorConfigurationReducer<T = TwoFactorConfigurationState> = Reducer<TwoFactorConfigurationState, T>;

export interface TwoFactorConfigurationState {
  config: Nullable<TwoFactorConfig>;
  isLoading: boolean;
  isAlertOpen: boolean;
  trace: Trace;
}

export const initialState: TwoFactorConfigurationState = {
  config: null,
  isLoading: false,
  isAlertOpen: false,
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
};

type DataFetchSucceededPayload = TwoFactorConfig;
type SetConfigSucceededPayload = TwoFactorConfig;
type ToggleConfigPayload = boolean;

const DATA_FETCH_STARTED = 'twoFactorConfiguration/DATA_FETCH_STARTED';
const DATA_FETCH_SUCCEEDED = 'twoFactorConfiguration/DATA_FETCH_SUCCEEDED';
const DATA_FETCH_FAILED = 'twoFactorConfiguration/DATA_FETCH_FAILED';

const CONFIG_TOGGLE_STARTED = 'twoFactorConfiguration/CONFIG_TOGGLE_STARTED';
const CONFIG_TOGGLE_SUCCEEDED = 'twoFactorConfiguration/CONFIG_TOGGLE_SUCCEEDED';
const CONFIG_TOGGLE_FAILED = 'twoFactorConfiguration/CONFIG_TOGGLE_FAILED';

const ALERT_CLOSE = 'twoFactorConfiguration/ALERT_CLOSE';

const STATE_RESET = 'twoFactorConfiguration/STATE_RESET';

export const fetchData = createAction(DATA_FETCH_STARTED);
export const fetchDataSucceeded = createAction<DataFetchSucceededPayload>(DATA_FETCH_SUCCEEDED);
export const fetchDataFailed = createAction(DATA_FETCH_FAILED);

export const toggleConfig = createAction<ToggleConfigPayload>(CONFIG_TOGGLE_STARTED);
export const toggleConfigSucceeded = createAction<SetConfigSucceededPayload>(CONFIG_TOGGLE_SUCCEEDED);
export const toggleConfigFailed = createAction(CONFIG_TOGGLE_FAILED);

export const closeAlert = createAction(ALERT_CLOSE);

export const resetState = createAction(STATE_RESET);

const handleFetchStarted: TwoFactorConfigurationReducer = state => ({
  ...state,
  isLoading: true,
});

const handleFetchSucceeded: TwoFactorConfigurationReducer<DataFetchSucceededPayload> = (state, { payload }) => ({
  ...state,
  isLoading: false,
  config: payload,
});

const handleFetchFailed: TwoFactorConfigurationReducer = state => ({
  ...state,
  isLoading: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: t('error.an-error-occurred.try-again-later'),
  },
});

const handleToggleConfigStarted: TwoFactorConfigurationReducer = state => ({
  ...state,
  isLoading: true,
});

const handleToggleConfigSucceeded: TwoFactorConfigurationReducer<SetConfigSucceededPayload> = (state, { payload }) => ({
  ...state,
  config: payload,
  isLoading: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Info,
    message: payload.enabled ? t('security-package.2fa.status.enabled') : t('security-package.2fa.status.disabled'),
  },
});

const handleToggleConfigFailed: TwoFactorConfigurationReducer = state => ({
  ...state,
  isLoading: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: t('error.an-error-occurred.try-again-later'),
  },
});

const handleCloseAlert: TwoFactorConfigurationReducer = state => ({
  ...state,
  isAlertOpen: false,
});

const handleStateReset: TwoFactorConfigurationReducer = state => ({
  ...state,
  isLoading: false,
  isAlertOpen: false,
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
});

export default handleActions<TwoFactorConfigurationState, any>(
  {
    [DATA_FETCH_STARTED]: handleFetchStarted,
    [DATA_FETCH_SUCCEEDED]: handleFetchSucceeded,
    [DATA_FETCH_FAILED]: handleFetchFailed,
    [CONFIG_TOGGLE_STARTED]: handleToggleConfigStarted,
    [CONFIG_TOGGLE_SUCCEEDED]: handleToggleConfigSucceeded,
    [CONFIG_TOGGLE_FAILED]: handleToggleConfigFailed,
    [ALERT_CLOSE]: handleCloseAlert,
    [STATE_RESET]: handleStateReset,
  },
  initialState,
);

export const getTwoFactorConfigurationState: SelectorState<TwoFactorConfigurationState> = state =>
  state.twoFactorConfiguration;
